import type { FC, ReactNode } from 'preact/compat';
import { useMemo, useState } from 'preact/compat';

import type { EditorLockType } from '../../types/editor';
import type { Message } from '../../types/messages';
import type { WidgetTheme } from '../../types/theme';
import type { WidgetLocale } from '../../types/widgetLocale';
import { getWidgetMessages, parseWidgetLocale, getBooleanFromStorage } from '../../utils';

import { HAS_MAX_USER_PHONE } from '../../constants/localStorage';
import { WidgetContext } from './WidgetContext';

interface Props {
  children: ReactNode;
  widgetTitle?: string;
  companyLogoSrc?: string;
  widgetUuid?: string;
  widgetTheme?: WidgetTheme;
  entryPointGateway?: string;
  requirePhone?: boolean;
  hideTriggerButton?: boolean;
  widgetLocale?: string;
}

const WidgetProvider: FC<Props> = ({
  children,
  companyLogoSrc,
  widgetTitle,
  widgetUuid,
  widgetTheme,
  entryPointGateway,
  requirePhone,
  hideTriggerButton = false,
  widgetLocale: widgetLocaleProp,
}) => {
  const locale: WidgetLocale = parseWidgetLocale(widgetLocaleProp);
  const localeMessages = useMemo(() => getWidgetMessages(locale), [locale]);

  // Сообщения в чате
  const [messages, setMessages] = useState<Message[]>([]);
  // Файлы, прикрепляемые к сообщению
  const [files, setFiles] = useState<File[]>([]);
  // Отключение области с написанием сообщения, прикреплением файлов и отправкой эмоджи
  const [editorLock, setEditorLock] = useState<EditorLockType | null>(null);
  // Ошибки в виджете
  const [error, setError] = useState<string | null>(null);
  // Нотификация о новых сообщениях
  const [hasNewMessage, setHasNewMessage] = useState(false);
  // Восстанавливаем hasMaxUserPhone из localStorage при инициализации
  const [hasMaxUserPhone, setHasMaxUserPhone] = useState<boolean | undefined>(() =>
    getBooleanFromStorage(HAS_MAX_USER_PHONE)
  );

  const defaultProps = useMemo(() => {
    return {
      messages,
      setMessages,
      files,
      setFiles,
      companyLogoSrc,
      widgetTitle,
      widgetTheme,
      widgetUuid,
      editorLock,
      entryPointGateway,
      requirePhone,
      hasMaxUserPhone,
      setHasMaxUserPhone,
      setEditorLock,
      error,
      setError,
      hasNewMessage,
      setHasNewMessage,
      hideTriggerButton,
      locale,
      localeMessages,
    };
  }, [
    messages,
    files,
    companyLogoSrc,
    widgetTitle,
    widgetTheme,
    widgetUuid,
    editorLock,
    error,
    entryPointGateway,
    requirePhone,
    hasMaxUserPhone,
    hasNewMessage,
    hideTriggerButton,
    locale,
    localeMessages,
  ]);

  return <WidgetContext.Provider value={defaultProps}>{children}</WidgetContext.Provider>;
};

export { WidgetProvider };
